import { type CanvasElement, type PropsOf } from '@blaze-runtime/engine-domain';
import { isNullish } from '@blaze-shared/shared-util';

import { getActiveInputDataSource } from '../../../canvas-elements/element-model/data-binding';
import { isElementType } from '../../../canvas-elements/element-model/element-type-guards';

/** Number formats whose fields offer a minimum/maximum in the properties editor. */
const RANGED_INPUT_FORMATS = new Set(['currency', 'percentage', 'number']);

type TextInputSettings = PropsOf<'textInput'>['content']['input'];

interface MinimumRule {
  min: number;
  /** What the author typed, when it differs from the compared value (percentages). */
  validationDisplayValueMin?: number;
}

interface MaximumRule {
  max: number;
  /** What the author typed, when it differs from the compared value (percentages). */
  validationDisplayValueMax?: number;
}

export type RangeRules = Partial<MinimumRule | MaximumRule>;

/** The range an author configured for one field, together with the format it applies to. */
export interface PropEditorFieldValidation {
  format: string | undefined;
  validationRules: RangeRules;
}

/** Range rules for a form's fields, keyed by column name. */
export type PropEditorFieldValidations = Record<string, PropEditorFieldValidation>;

/**
 * Collects the minimum/maximum rules the author configured on the text inputs bound to a
 * form. They live in element props rather than in the schema, so validation has to read
 * them off the form's own fields.
 *
 * @param boundElements the elements bound to the form
 * @param getColumnName resolves a binding's field key to the column name it points at
 */
export function collectPropEditorValidationRules(
  boundElements: CanvasElement[] | undefined,
  getColumnName: (fieldKey: string) => string | undefined,
): PropEditorFieldValidations {
  const validations: PropEditorFieldValidations = {};

  for (const element of boundElements ?? []) {
    if (!isElementType(element, 'textInput')) {
      continue;
    }

    const fieldKey = getFormlikeFieldKey(element);
    const columnName = fieldKey ? getColumnName(fieldKey) : undefined;
    if (!columnName) {
      continue;
    }

    const input = element.props.content.input;
    const validationRules = readRangeRules(input);
    if (validationRules) {
      validations[columnName] = { format: input?.format, validationRules };
    }
  }

  return validations;
}

/**
 * Checks a value against the range its field declares.
 * @returns the broken rules, with the value to show the user; empty when within range
 */
export function validatePropEditorRange(
  columnValue: string | number,
  validation: PropEditorFieldValidation | undefined,
): Record<string, number> {
  const errors: Record<string, number> = {};
  const rules = validation?.validationRules;
  if (!rules || !RANGED_INPUT_FORMATS.has(validation?.format ?? '') || isNullish(columnValue)) {
    return errors;
  }

  if (hasMinimum(rules) && Number(columnValue) < rules.min) {
    errors.min = Number(rules.validationDisplayValueMin ?? rules.min);
  }
  if (hasMaximum(rules) && Number(columnValue) > rules.max) {
    errors.max = Number(rules.validationDisplayValueMax ?? rules.max);
  }

  return errors;
}

/** The field an input writes into, or `undefined` when it is not bound to a formlike. */
function getFormlikeFieldKey(element: CanvasElement): string | undefined {
  const dataBinding = getActiveInputDataSource(element);
  if (dataBinding?.sourceType !== 'formContainer' && dataBinding?.sourceType !== 'editableList') {
    return undefined;
  }
  return dataBinding.name;
}

/** @returns the boundaries configured on a text input, or `undefined` when it has none */
function readRangeRules(input: TextInputSettings): RangeRules | undefined {
  const minimum = readBoundary(input?.format, input?.minimum);
  const maximum = readBoundary(input?.format, input?.maximum);
  if (!minimum && !maximum) {
    return undefined;
  }

  return {
    ...(minimum
      ? {
          min: minimum.compared,
          ...(minimum.displayed === undefined
            ? {}
            : { validationDisplayValueMin: minimum.displayed }),
        }
      : {}),
    ...(maximum
      ? {
          max: maximum.compared,
          ...(maximum.displayed === undefined
            ? {}
            : { validationDisplayValueMax: maximum.displayed }),
        }
      : {}),
  };
}

/**
 * Reads one configured boundary off a text input's settings.
 * @returns the value to compare against plus the one to display to the user, or
 * `undefined` when the input declares no boundary for this format
 */
function readBoundary(
  format: string | undefined,
  boundary: string | number | undefined,
): { compared: number; displayed: number | undefined } | undefined {
  if (
    !format ||
    !RANGED_INPUT_FORMATS.has(format) ||
    boundary === '' ||
    isNullish(boundary) ||
    (typeof boundary === 'number' && Number.isNaN(boundary))
  ) {
    return undefined;
  }

  const configured = Number(boundary);
  // why: a percentage is authored as 0-100 but stored (and compared) as a fraction
  return format === 'percentage'
    ? { compared: configured / 100, displayed: configured }
    : { compared: configured, displayed: undefined };
}

function hasMinimum(rule: RangeRules): rule is MinimumRule {
  return 'min' in rule;
}

function hasMaximum(rule: RangeRules): rule is MaximumRule {
  return 'max' in rule;
}
